import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Eye, Trash2, MailCheck, Loader2 } from 'lucide-react';
import { Section, Modal } from './_shared';

export default function InquiriesPanel({ cx }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);
  const [status, setStatus] = useState(null);
  const [testing, setTesting] = useState(false);

  const load = async () => { setLoading(true); try { const r = await cx.get('/inquiries'); setItems(r.data || []); } finally { setLoading(false); } };
  const loadStatus = async () => { try { const r = await cx.get('/admin/resend/status'); setStatus(r.data); } catch { /* admin may not have it yet */ } };
  useEffect(() => { load(); loadStatus(); }, []);

  const del = async (id) => {
    if (!window.confirm('Delete this inquiry?')) return;
    try { await cx.delete(`/inquiries/${id}`); toast.success('Deleted'); load(); } catch { toast.error('Delete failed'); }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const r = await cx.post('/admin/resend/test');
      toast.success(`Test email sent to ${r.data.recipient}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Resend test failed');
    } finally { setTesting(false); }
  };

  return (
    <Section title="Inquiries" action={<button onClick={load} className="btn-secondary text-sm">Refresh</button>}>
      {/* Resend health card */}
      <div data-testid="admin-resend-card" className="border border-subtle surface rounded-md p-4 mb-5 flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="font-display text-prim flex items-center gap-2"><MailCheck size={16} className="text-brand-teal-light" /> Resend delivery</p>
          <p className="text-sec text-xs font-mono mt-1 truncate">
            {status ? (
              <>
                {status.configured ? <span className="text-brand-teal-light">API key configured</span> : <span className="text-brand-maroon-light">API key missing</span>}
                {' · '}from <span className="text-prim">{status.sender}</span>
                {' · '}to <span className="text-prim">{status.recipient}</span>
              </>
            ) : 'Loading status…'}
          </p>
          <p className="text-xs text-muted mt-1">
            If the test fails with a domain error, verify <span className="font-mono">mbkholding.qa</span> on resend.com → Domains. Inquiries still save here regardless.
          </p>
        </div>
        <button
          data-testid="admin-resend-test"
          onClick={sendTest}
          disabled={testing}
          className="btn-primary text-sm whitespace-nowrap disabled:opacity-60"
        >
          {testing ? <Loader2 size={14} className="animate-spin" /> : <MailCheck size={14} />}
          {testing ? 'Sending…' : 'Send test email'}
        </button>
      </div>

      {loading ? <p className="text-sec text-sm font-mono">Loading…</p> : items.length === 0 ? <p className="text-muted text-sm">No inquiries yet.</p> : (
        <div className="space-y-2">
          {items.map((i) => (
            <div key={i.id} data-testid={`admin-inquiry-row-${i.id}`} className="border border-subtle surface rounded-md p-4 flex justify-between items-start gap-3">
              <div className="min-w-0">
                <p className="font-display text-prim">{i.subject}</p>
                <p className="text-sm text-sec mt-1 truncate">{i.name} · {i.email}{i.organization ? ` · ${i.organization}` : ''}</p>
                <p className="text-xs text-muted font-mono mt-1">{i.created_at}</p>
              </div>
              <div className="flex gap-2 shrink-0">
                <button onClick={() => setOpen(i)} className="text-xs text-brand-teal-light hover:underline flex items-center gap-1"><Eye size={12} /> View</button>
                <button onClick={() => del(i.id)} className="text-xs text-brand-maroon-light hover:underline flex items-center gap-1"><Trash2 size={12} /> Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}
      {open && (
        <Modal onClose={() => setOpen(null)} title={open.subject}>
          <div className="space-y-3 text-sm">
            <p><span className="text-muted">From:</span> <span className="text-prim">{open.name} ({open.email})</span></p>
            {open.organization && <p><span className="text-muted">Organisation:</span> <span className="text-prim">{open.organization}</span></p>}
            <p><span className="text-muted">Type:</span> <span className="text-prim">{open.inquiry_type}</span></p>
            <p><span className="text-muted">Received:</span> <span className="text-prim font-mono text-xs">{open.created_at}</span></p>
            <div className="border-t border-subtle pt-3 mt-3">
              <p className="text-muted text-xs mb-2">Message</p>
              <p className="text-prim whitespace-pre-wrap">{open.message}</p>
            </div>
          </div>
        </Modal>
      )}
    </Section>
  );
}
